// Mieterhöhung bis zur ortsüblichen Vergleichsmiete (§ 558 BGB) – Fristen und Kappungsgrenze.
// Nur aus Daten, die es gibt: Mietstände (miet_historie) bzw. Kaltmiete und Mietbeginn.
// Der Mietspiegel fehlt (Fehlende Daten) – daher kein Betrag „bis zur Vergleichsmiete“,
// sondern der rechtliche Rahmen: wann frühestens, und wie viel höchstens (Kappungsgrenze).

import { leseStand, sortiereStaende } from '../mieter/mietstaende';

export type Kappung = 15 | 20;

export interface MieterhoehungAnalyse {
  kaltAktuell: number;
  /** letzte Änderung der Kaltmiete (oder Mietbeginn), YYYY-MM-DD */
  letzteAenderung: string | null;
  /** § 558 Abs. 1 S. 2: Verlangen frühestens ein Jahr nach der letzten Erhöhung */
  fruehestesVerlangen: string;
  /** § 558 Abs. 1 S. 1 + § 558b Abs. 1: frühestens 15 Monate nach der letzten Änderung, Beginn des 3. Monats nach Zugang */
  fruehesteWirksamkeit: string;
  /** Stichtag drei Jahre vor der Wirksamkeit (Kappungsgrenze § 558 Abs. 3) */
  basisDatum: string;
  basisKalt: number;
  kappung: Kappung;
  maxKalt: number;
  /** höchstens zulässige Erhöhung pro Monat nach der Kappungsgrenze */
  spielraum: number;
  /** ein Mietstand liegt in der Zukunft (z. B. Staffel) – § 558 ist dann meist ausgeschlossen */
  geplanteAenderung: string | null;
  kaltProQm: number | null;
}

export interface MietRoh {
  kaltmiete: unknown;
  miet_historie: unknown;
  mietbeginn: string | null;
}

const zahl = (v: unknown): number => { const n = Number(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : 0; };
const runde = (n: number) => Math.round(n * 100) / 100 || 0;

/** ISO-Datum + n Monate; Tag wird auf das Monatsende begrenzt (31.01. + 1 = 28./29.02.). */
export function plusMonate(iso: string, n: number): string {
  const [j, m, t] = iso.slice(0, 10).split('-').map(Number) as [number, number, number];
  const gesamt = j * 12 + (m - 1) + n;
  const jj = Math.floor(gesamt / 12);
  const mm = gesamt - jj * 12 + 1;
  const letzter = new Date(Date.UTC(jj, mm, 0)).getUTCDate();
  return `${jj}-${String(mm).padStart(2, '0')}-${String(Math.min(t, letzter)).padStart(2, '0')}`;
}

/** Erster Tag des n-ten Monats nach dem Monat von iso. */
export function monatsersterPlus(iso: string, n: number): string {
  return `${plusMonate(`${iso.slice(0, 7)}-01`, n)}`;
}

const spaeter = (a: string, b: string) => (a > b ? a : b);

export function analyseMieterhoehung(m: MietRoh, heute: string, flaeche: number, kappung: Kappung = 20): MieterhoehungAnalyse {
  const roh = Array.isArray(m.miet_historie) ? m.miet_historie.map((r, i) => leseStand(r, i)) : [];
  let staende = sortiereStaende(roh.filter(s => s.gueltigAb)).map(s => ({ ab: s.gueltigAb.slice(0, 10), kalt: s.kaltmiete }));
  if (staende.length === 0) staende = [{ ab: (m.mietbeginn ?? '').slice(0, 10), kalt: zahl(m.kaltmiete) }];

  const bisHeute = staende.filter(s => !s.ab || s.ab <= heute);
  const kaltAm = (datum: string): number => {
    const passend = staende.filter(s => !s.ab || s.ab <= datum);
    return (passend[passend.length - 1] ?? staende[0])?.kalt ?? 0;
  };
  const kaltAktuell = bisHeute.length ? bisHeute[bisHeute.length - 1]!.kalt : zahl(m.kaltmiete);

  // letzte echte Änderung der Kaltmiete (gleichbleibende Stände, z. B. nur NK geändert, zählen nicht)
  let letzteAenderung: string | null = bisHeute[0]?.ab || (m.mietbeginn ?? '').slice(0, 10) || null;
  for (let i = 1; i < bisHeute.length; i++) if (bisHeute[i]!.kalt !== bisHeute[i - 1]!.kalt) letzteAenderung = bisHeute[i]!.ab;

  const fruehestesVerlangen = letzteAenderung ? spaeter(heute, plusMonate(letzteAenderung, 12)) : heute;
  const ausVerlangen = monatsersterPlus(fruehestesVerlangen, 3);
  const fruehesteWirksamkeit = letzteAenderung ? spaeter(ausVerlangen, plusMonate(letzteAenderung, 15)) : ausVerlangen;
  const basisDatum = plusMonate(fruehesteWirksamkeit, -36);
  const basisKalt = kaltAm(basisDatum);
  const maxKalt = runde(basisKalt * (1 + kappung / 100));
  const geplant = staende.find(s => s.ab && s.ab > heute);

  return {
    kaltAktuell,
    letzteAenderung,
    fruehestesVerlangen,
    fruehesteWirksamkeit,
    basisDatum,
    basisKalt,
    kappung,
    maxKalt,
    spielraum: Math.max(0, runde(maxKalt - kaltAktuell)),
    geplanteAenderung: geplant?.ab ?? null,
    kaltProQm: flaeche > 0 && kaltAktuell > 0 ? runde(kaltAktuell / flaeche) : null,
  };
}
